import Stack from '@mui/material/Stack';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { controlledTextField } from 'components/ReactHookForm/controlledTextField';
import {
  Modal,
  ModalContents,
  ModalTitle,
  ModalTypography,
  ModalButton,
} from 'components/Modal/Modal';
import { showErrorAction } from 'containers/Notification/actions';
import {
  useUpdateNonprofitNominationMutation,
  NonprofitNominationStatusType,
} from 'generated/graphql';

import { NOMINATION_STATUS, NonprofitNominationInput } from './constants';

type FieldValues = {
  nonprofitLink?: string;
};

const NonprofitLinkInput = controlledTextField<FieldValues>()(
  NonprofitNominationInput.nonprofitLink
);

const modalInputs = {
  [NonprofitNominationInput.nonprofitLink]: ({ control, errors, ...props }) => (
    <NonprofitLinkInput
      controllerProps={{ control, rules: { required: true } }}
      helperText={
        errors.identifier?.message ||
        'A link to or internal/external identifier of the found nonprofit'
      }
      label="Nonprofit link/identifier"
      type="text"
      {...props}
    />
  ),
};

const StatusModal = ({
  handleCloseStatusModal,
  updatedNominationStatus,
  nominationId,
}: {
  handleCloseStatusModal: () => void;
  updatedNominationStatus: NonprofitNominationStatusType;
  nominationId: string;
}): React.ReactElement => {
  const dispatch = useDispatch();
  const [updateNonprofitNominationMutation] = useUpdateNonprofitNominationMutation();
  const {
    handleSubmit: reactHookFormHandleSubmit,
    formState: { errors, isValid, isSubmitting },
    control,
  } = useForm<FieldValues>({
    mode: 'onChange',
    defaultValues: {
      nonprofitLink: '',
    },
  });

  const modalContent = NOMINATION_STATUS[updatedNominationStatus];

  const handleSubmit = reactHookFormHandleSubmit(async ({ nonprofitLink }: FieldValues) => {
    try {
      await updateNonprofitNominationMutation({
        variables: {
          updateNonprofitNominationId: nominationId,
          data: {
            status: updatedNominationStatus,
            nonprofitLink,
          },
        },
      });
      handleCloseStatusModal();
    } catch (e) {
      dispatch(showErrorAction('There was an error updating this nomination'));
    }
  });

  return (
    <Modal open disableAutoFocus>
      <ModalContents
        onClickClose={handleCloseStatusModal}
        body={
          <>
            <ModalTitle>Nonprofit Nomination: {modalContent.name}</ModalTitle>
            <ModalTypography>{modalContent.copy}</ModalTypography>
            <ModalTypography>Do you wish to continue with this update?</ModalTypography>
            <Stack>
              {modalContent.inputs?.map((inputKey) => {
                const Input = modalInputs[inputKey];

                return (
                  <Input
                    inputKey={inputKey}
                    updatedNominationStatus={updatedNominationStatus}
                    control={control}
                    errors={errors}
                  />
                );
              })}
            </Stack>
          </>
        }
        actions={
          <>
            <ModalButton
              disabled={isSubmitting}
              onClick={handleCloseStatusModal}
              variant="outlined"
            >
              No, cancel update
            </ModalButton>
            &nbsp;
            <ModalButton disabled={!isValid || isSubmitting} onClick={handleSubmit}>
              Yes, continue with update
            </ModalButton>
          </>
        }
      />
    </Modal>
  );
};

export default StatusModal;
